import { z } from "zod";

/**
 * "Email it": a proposal, change order, or invoice sent to the customer by email from the CRM,
 * from the rep's own address, with the link added as a button. The wording comes from templates
 * in Settings → Email templates, filled in and edited before sending.
 */

export const EMAIL_DOC_KINDS = ["proposal", "change_order", "invoice"] as const;
export type EmailDocKind = (typeof EMAIL_DOC_KINDS)[number];

export const EMAIL_DOC_LABELS: Record<EmailDocKind, string> = { proposal: "Proposal", change_order: "Change order", invoice: "Invoice" };

/** What can go in a template: {first_name} and so on. */
export const EMAIL_PLACEHOLDERS = ["first_name", "customer_name", "title", "amount", "rep_name", "rep_phone", "company", "address", "balance", "due_date"] as const;
export type EmailVars = Record<(typeof EMAIL_PLACEHOLDERS)[number], string>;

export const emailTemplateSchema = z.object({
  subject: z.string().trim().min(1).max(200),
  body: z.string().trim().min(1).max(5000),
  /** The words on the link's button. */
  button: z.string().trim().min(1).max(60),
});
export type EmailTemplate = z.infer<typeof emailTemplateSchema>;
export const emailTemplatesSchema = z.object({ proposal: emailTemplateSchema, change_order: emailTemplateSchema, invoice: emailTemplateSchema });
export type EmailTemplates = z.infer<typeof emailTemplatesSchema>;

export const DEFAULT_EMAIL_TEMPLATES: EmailTemplates = {
  proposal: {
    subject: "Your proposal from {company}: {title}",
    body: "Hi {first_name},\n\nThank you for the opportunity to earn your business. Your proposal for {title} at {address} is ready to review and sign online. You'll initial each page and sign at the end; it only takes a few minutes.\n\nIf you have any questions, just reply to this email or call me at {rep_phone}.\n\nThank you,\n{rep_name}\n{company}",
    button: "Review and sign",
  },
  change_order: {
    subject: "Change order for your project: {title}",
    body: "Hi {first_name},\n\nHere's the change order we discussed ({title}, {amount}). Please review and sign it online when you have a moment.\n\nIf you have any questions, just reply to this email or call me at {rep_phone}.\n\nThank you,\n{rep_name}\n{company}",
    button: "Review and sign",
  },
  invoice: {
    subject: "Invoice from {company}: {title}",
    body: "Hi {first_name},\n\nYour invoice for {title} ({amount}) is ready. You can view it and pay online by card or bank transfer.\n\nIf you have any questions, just reply to this email or call me at {rep_phone}.\n\nThank you,\n{rep_name}\n{company}",
    button: "View and pay",
  },
};

/** "Text it": the text for each kind; the link is added at the end. */
export const textTemplatesSchema = z.object({
  proposal: z.string().trim().min(1).max(600),
  change_order: z.string().trim().min(1).max(600),
  invoice: z.string().trim().min(1).max(600),
});
export type TextTemplates = z.infer<typeof textTemplatesSchema>;

export const DEFAULT_TEXT_TEMPLATES: TextTemplates = {
  proposal: "Hi {first_name}, it's {rep_name} with {company}. Your proposal for {title} is ready to review and sign here:",
  change_order: "Hi {first_name}, it's {rep_name} with {company}. Here's the change order for {title} ({amount}) to review and sign:",
  invoice: "Hi {first_name}, it's {rep_name} with {company}. Your invoice for {title} ({amount}) is ready. You can view and pay it here:",
};

/** A past-due invoice reminder ("Send reminder" on the Past due card): its own email and text. */
export const DEFAULT_OVERDUE_EMAIL: EmailTemplate = {
  subject: "Reminder: invoice {title} from {company} is past due",
  body: "Hi {first_name},\n\nThis is a friendly reminder that your invoice for {title} was due {due_date}, and {balance} is still open. You can view it and pay online by card or bank transfer with the button below.\n\nIf you've already sent payment, thank you, and please disregard this note. If you have any questions, just reply to this email or call me at {rep_phone}.\n\nThank you,\n{rep_name}\n{company}",
  button: "View and pay",
};
export const DEFAULT_OVERDUE_TEXT = "Hi {first_name}, it's {rep_name} with {company}. A friendly reminder that {balance} on your invoice for {title} was due {due_date}. You can view and pay it here:";

export const docTextSchema = z.object({
  to: z.string().trim().min(7, "Add their cell number").max(30),
  message: z.string().trim().min(1, "Write a message").max(900),
});

/** The "Text it" window, filled in from the template (the link goes at the end when sent). */
export interface DocTextDraft {
  to: string;
  message: string;
  link: string;
  /** From which number, as the customer sees it. */
  from: string;
  sending: { ready: boolean; reason: string };
}

/** Fills {placeholders}; unknown ones are left as typed. */
export function fillTemplate(text: string, vars: Partial<EmailVars>): string {
  return text.replace(/\{([a-z_]+)\}/g, (m, k: string) => (k in vars ? (vars as Record<string, string>)[k] ?? "" : m));
}

const address = z.string().trim().toLowerCase().email("Check the email address").max(254);

export const docEmailSchema = z.object({
  to: address,
  cc: z.array(address).max(5).default([]),
  subject: z.string().trim().min(1, "Add a subject").max(200),
  message: z.string().trim().min(1, "Add a message").max(5000),
});
export type DocEmailInput = z.infer<typeof docEmailSchema>;

/** The "Email it" window, filled in from the template. */
export interface DocEmailDraft {
  to: string;
  cc: string[];
  subject: string;
  message: string;
  button: string;
  link: string;
  /** Who it's from, as the customer sees it ("Jason Seapy · NMT Construction <jason@nmtconstruction.com>"). */
  from: string;
  /** "ready" sends from the CRM; otherwise the window offers the mail app instead, and says why. */
  sending: { ready: boolean; reason: string };
}

/** An email on a job's Communication tab. */
export interface EmailItem {
  id: string;
  direction: "out" | "in";
  docKind: EmailDocKind | null;
  docLabel: string;
  from: string;
  to: string[];
  cc: string[];
  subject: string;
  body: string;
  status: "sent" | "failed" | "bounced" | "complaint" | "received";
  error: string | null;
  at: string;
  sentBy: { id: string; name: string } | null;
  /** Staging: it went to this test address instead. */
  redirectedTo: string | null;
}
